// ─────────────────────────────────────────────
// KLOTHIQ — Cashbook Excel import helpers (FEAT-ACCOUNTS-001, FR-018)
// Pure functions: parse a sheet (array-of-arrays from SheetJS), auto-classify
// rows (BR-006), apply the cut-off (BR-007) and compute preview totals.
// ─────────────────────────────────────────────

export type ImportKind = 'capital' | 'sale' | 'purchase' | 'expense';

export type ImportStatus = 'ok' | 'needs_fix' | 'needs_review' | 'skipped';

export interface RawImportRow {
  /** 1-based row number in the sheet, for error messages. */
  row_no: number;
  date: string | null;          // ISO yyyy-mm-dd
  particular: string;
  income: number | null;
  expense: number | null;
}

export interface PreviewRow {
  row_no: number;
  date: string | null;
  particular: string;
  amount: number | null;
  /** 'income' when it came from the Income column, 'expense' from Expense. */
  side: 'income' | 'expense' | null;
  kind: ImportKind | null;
  partner_id: string | null;
  /** Name parsed from "NAME(CAPITAL)" — used to offer "create partner". */
  partner_hint: string | null;
  expense_category_id: string | null;
  supplier_hint: string | null;
  /** User chose to leave this row out. */
  excluded: boolean;
}

export interface EvaluatedRow extends PreviewRow {
  status: ImportStatus;
  issues: string[];
}

export interface PartnerRef { id: string; name: string }
export interface CategoryRef { id: string; name: string; keywords: string[]; sort_order?: number; is_active?: boolean }

export interface ColumnMap { date: number; particular: number; income: number; expense: number }

export interface SheetTotals { income: number | null; expense: number | null; balance: number | null }

// ── Parsing primitives ───────────────────────────────────────

function pad(n: number): string { return String(n).padStart(2, '0'); }

function isValidYMD(y: number, m: number, d: number): boolean {
  if (y < 1900 || y > 2999 || m < 1 || m > 12 || d < 1) return false;
  const dim = new Date(y, m, 0).getDate();
  return d <= dim;
}

/**
 * Accepts DD/MM/YYYY, D/M/YYYY, DD-MM-YYYY, YYYY-MM-DD, JS Date and Excel
 * serial numbers. Returns ISO yyyy-mm-dd or null. Day-first (BR-008).
 */
export function parseSheetDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Excel serial date (1900 system). 25569 = 1970-01-01.
    if (value < 20000 || value > 80000) return null;
    const ms = Math.round((value - 25569) * 86400 * 1000);
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  }

  const s = String(value).trim();
  let m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) {
    const d = Number(m[1]); const mo = Number(m[2]); const y = Number(m[3]);
    return isValidYMD(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
  }
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
    return isValidYMD(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
  }
  return null;
}

/** "₹ 1,234.50" → 1234.5 ; blank → null ; non-numeric → NaN */
export function parseAmount(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const s = String(value).replace(/[₹,\s]/g, '').replace(/^rs\.?/i, '');
  if (s === '' || s === '-' || s === '—') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString();
  return String(v).trim();
}

// ── Sheet → raw rows ─────────────────────────────────────────

/** Finds the header row (the first row with a "PARTICULAR" cell) and the column indices. */
export function detectColumns(aoa: unknown[][]): { headerRow: number; map: ColumnMap } | null {
  for (let r = 0; r < Math.min(aoa.length, 25); r++) {
    const row = aoa[r] ?? [];
    const lower = row.map((c) => cellText(c).toLowerCase());
    const particular = lower.findIndex((c) => c.startsWith('particular'));
    if (particular === -1) continue;
    const income = lower.findIndex((c) => c.startsWith('income') || c === 'in' || c.startsWith('receipt'));
    const expense = lower.findIndex((c) => c.startsWith('expens') || c === 'out' || c.startsWith('payment'));
    let date = lower.findIndex((c) => c.includes('date'));
    if (date === -1) date = particular > 0 ? particular - 1 : 0;
    if (income === -1 || expense === -1) return null;
    return { headerRow: r, map: { date, particular, income, expense } };
  }
  return null;
}

/**
 * Converts an array-of-arrays to raw rows. Fully blank rows are dropped. A row
 * with no date and no particular but BOTH income and expense filled is treated
 * as the sheet's totals row: it is returned separately (for reconciliation),
 * not as data.
 */
export function extractRows(aoa: unknown[][], headerRow: number, map: ColumnMap): { rows: RawImportRow[]; sheetTotals: SheetTotals | null } {
  const rows: RawImportRow[] = [];
  let sheetTotals: SheetTotals | null = null;

  for (let r = headerRow + 1; r < aoa.length; r++) {
    const row = aoa[r] ?? [];
    const rawDate = row[map.date];
    const particular = cellText(row[map.particular]);
    const income = parseAmount(row[map.income]);
    const expense = parseAmount(row[map.expense]);
    const date = parseSheetDate(rawDate);
    const hasDateCell = cellText(rawDate) !== '';

    if (!hasDateCell && particular === '' && income === null && expense === null) continue;

    if (!hasDateCell && particular === '' && income !== null && expense !== null) {
      const balance = parseAmount(row[map.expense + 1]);
      sheetTotals = {
        income: Number.isNaN(income) ? null : income,
        expense: Number.isNaN(expense) ? null : expense,
        balance: balance === null || Number.isNaN(balance) ? null : balance,
      };
      continue;
    }

    rows.push({
      row_no: r + 1,
      date: date ?? (hasDateCell ? `invalid:${cellText(rawDate)}` : null),
      particular,
      income,
      expense,
    });
  }
  return { rows, sheetTotals };
}

// ── Classification (BR-006) ──────────────────────────────────

const CAPITAL_RE = /^(.+?)\s*\(\s*capital\s*\)\s*$/i;
const PURCHASE_RE = /^(.*?)\s*\(\s*purchase\s*\)\s*$/i;
const SALES_RE = /\bsales?\b/i;

function normaliseName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function matchPartner(hint: string | null, partners: PartnerRef[]): string | null {
  if (!hint) return null;
  const key = normaliseName(hint);
  if (!key) return null;
  return partners.find((p) => normaliseName(p.name) === key)?.id ?? null;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** First active category (by sort_order) whose name or keyword appears as a whole word. */
export function matchCategory(particular: string, categories: CategoryRef[]): string | null {
  const text = particular.toLowerCase();
  const sorted = categories
    .filter((c) => c.is_active !== false)
    .slice()
    .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100));
  for (const c of sorted) {
    const terms = [...(c.keywords ?? []), c.name.toLowerCase()].filter(Boolean);
    for (const t of terms) {
      const re = new RegExp(`(^|[^a-z])${escapeRe(t.toLowerCase())}([^a-z]|$)`);
      if (re.test(text)) return c.id;
    }
  }
  return null;
}

export function findMiscCategory(categories: CategoryRef[]): string | null {
  return categories.find((c) => c.name.toLowerCase() === 'miscellaneous')?.id ?? null;
}

export function classifyRow(raw: RawImportRow, partners: PartnerRef[], categories: CategoryRef[]): PreviewRow {
  const incomeOk = raw.income !== null && !Number.isNaN(raw.income) && raw.income !== 0;
  const expenseOk = raw.expense !== null && !Number.isNaN(raw.expense) && raw.expense !== 0;
  const side: PreviewRow['side'] = incomeOk && !expenseOk ? 'income' : expenseOk && !incomeOk ? 'expense' : null;
  const amount = side === 'income' ? raw.income : side === 'expense' ? raw.expense : null;

  const base: PreviewRow = {
    row_no: raw.row_no,
    date: raw.date,
    particular: raw.particular,
    amount,
    side,
    kind: null,
    partner_id: null,
    partner_hint: null,
    expense_category_id: null,
    supplier_hint: null,
    excluded: false,
  };

  const p = raw.particular;
  const cap = p.match(CAPITAL_RE);
  if (cap && cap[1]) {
    const hint = cap[1].trim();
    return { ...base, kind: 'capital', partner_hint: hint, partner_id: matchPartner(hint, partners) };
  }
  const pur = p.match(PURCHASE_RE);
  if (pur) {
    const hint = pur[1] ? pur[1].trim() : null;
    return { ...base, kind: 'purchase', supplier_hint: hint || null };
  }
  if (side === 'income' && SALES_RE.test(p)) {
    return { ...base, kind: 'sale' };
  }
  if (side === 'expense') {
    return {
      ...base,
      kind: 'expense',
      expense_category_id: matchCategory(p, categories) ?? findMiscCategory(categories),
    };
  }
  return base; // income with unknown particular → needs review
}

// ── Evaluation (status + issues), cut-off (BR-007) ───────────

export function evaluateRow(row: PreviewRow, opts: { today: string; cutoff: string | null }): EvaluatedRow {
  const issues: string[] = [];

  if (row.excluded) return { ...row, status: 'skipped', issues: ['Excluded by you'] };

  if (!row.date) issues.push('Date missing');
  else if (row.date.startsWith('invalid:')) issues.push(`Unreadable date "${row.date.slice(8)}"`);
  else if (row.date > opts.today) issues.push('Date is in the future');

  if (!row.particular.trim()) issues.push('Particular missing');

  if (row.amount === null) issues.push('Fill exactly one of Income or Expense');
  else if (Number.isNaN(row.amount)) issues.push('Amount is not a number');
  else if (row.amount <= 0) issues.push('Amount must be greater than zero');
  else if (Math.round(row.amount * 100) !== row.amount * 100) issues.push('Amount has more than 2 decimals');

  if (row.kind === 'capital') {
    if (row.side === 'expense') issues.push('Capital must be in the Income column');
    if (!row.partner_id) issues.push(row.partner_hint ? `Map partner "${row.partner_hint}"` : 'Choose a partner');
  }
  if ((row.kind === 'sale') && row.side === 'expense') issues.push('Sales must be in the Income column');
  if ((row.kind === 'purchase' || row.kind === 'expense') && row.side === 'income') {
    issues.push(`${row.kind === 'purchase' ? 'Purchase' : 'Expense'} must be in the Expense column`);
  }

  if (issues.length > 0) return { ...row, status: 'needs_fix', issues };

  if (!row.kind) return { ...row, status: 'needs_review', issues: ['Choose a type'] };

  if (
    opts.cutoff &&
    (row.kind === 'sale' || row.kind === 'purchase') &&
    row.date !== null &&
    row.date >= opts.cutoff
  ) {
    return { ...row, status: 'skipped', issues: ['Already in system (on/after cut-off)'] };
  }

  return { ...row, status: 'ok', issues: [] };
}

export function evaluateRows(rows: PreviewRow[], opts: { today: string; cutoff: string | null }): EvaluatedRow[] {
  return rows.map((r) => evaluateRow(r, opts));
}

export interface PreviewTotals {
  income: number;
  expense: number;
  balance: number;
  capital: number;
  sales: number;
  purchases: number;
  expenses: number;
  counts: Record<ImportStatus, number>;
  /** True when no row blocks the import. */
  canImport: boolean;
}

export function previewTotals(rows: EvaluatedRow[]): PreviewTotals {
  const counts: Record<ImportStatus, number> = { ok: 0, needs_fix: 0, needs_review: 0, skipped: 0 };
  let capital = 0; let sales = 0; let purchases = 0; let expenses = 0;
  for (const r of rows) {
    counts[r.status] += 1;
    if (r.status !== 'ok' || r.amount === null) continue;
    if (r.kind === 'capital') capital += r.amount;
    else if (r.kind === 'sale') sales += r.amount;
    else if (r.kind === 'purchase') purchases += r.amount;
    else if (r.kind === 'expense') expenses += r.amount;
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const income = r2(capital + sales);
  const expense = r2(purchases + expenses);
  return {
    income,
    expense,
    balance: r2(income - expense),
    capital: r2(capital),
    sales: r2(sales),
    purchases: r2(purchases),
    expenses: r2(expenses),
    counts,
    canImport: counts.ok > 0 && counts.needs_fix === 0 && counts.needs_review === 0,
  };
}

export interface ImportPayloadRow {
  row_no: number;
  date: string;
  kind: ImportKind;
  amount: number;
  particular: string;
  partner_id: string | null;
  expense_category_id: string | null;
}

export function toImportPayload(rows: EvaluatedRow[]): ImportPayloadRow[] {
  return rows
    .filter((r) => r.status === 'ok' && r.kind && r.date && r.amount !== null)
    .map((r) => ({
      row_no: r.row_no,
      date: r.date as string,
      kind: r.kind as ImportKind,
      amount: r.amount as number,
      particular: r.particular.trim(),
      partner_id: r.kind === 'capital' ? r.partner_id : null,
      expense_category_id: r.kind === 'expense' ? r.expense_category_id : null,
    }));
}

/** Unmatched partner names that the user could create in one click. */
export function unmatchedPartnerHints(rows: PreviewRow[]): string[] {
  const set = new Map<string, string>();
  for (const r of rows) {
    if (r.kind === 'capital' && !r.partner_id && r.partner_hint && !r.excluded) {
      set.set(normaliseName(r.partner_hint), r.partner_hint);
    }
  }
  return Array.from(set.values());
}

/** SHA-256 hex of the file contents (EC-009 duplicate detection). */
export async function fileHash(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
